import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Clock, Phone, Trash2, RefreshCw, AlertCircle, CheckCircle, X, Search, Calendar } from 'lucide-react';

interface ScheduledJob {
  id: number;
  workflowId: number;
  executionId: number;
  workflowStepId: number;
  customerPhone: string;
  scheduledAt: number;
  status: string;
  payload: string;
  lockedAt: number | null;
  createdAt: number;
  processedAt: number | null;
  workflowName: string | null;
  workflowKey: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function AdminPendingJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const fetchJobs = async (page = pagination.page, phone = phoneFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status: 'pending', page: String(page), limit: String(pagination.limit) });
      if (phone.trim()) params.set('phone', phone.trim());
      const res = await apiFetch(`/api/public/admin/workflow-scheduled-jobs?${params.toString()}`);
      if (!res.ok) throw new Error('載入失敗');
      const payload = await res.json();
      const data = payload?.data ?? payload; // API 標準回應包裝在 {success, data} 內
      setJobs(data.jobs || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchJobs(1, '');
  }, [navigate]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchJobs(1, phoneFilter);
  };

  const handleCancel = async (job: ScheduledJob) => {
    if (!confirm(`確定取消發送給 ${job.customerPhone} 的排程訊息？\n這會永久刪除這個排程任務。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/workflow-scheduled-jobs/${job.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '取消失敗');
      setSuccess(`已取消發送給 ${job.customerPhone} 的排程訊息`);
      setTimeout(() => setSuccess(null), 3000);
      fetchJobs(pagination.page, phoneFilter);
    } catch (e: any) {
      setError(e.message || '取消失敗');
    }
  };

  const formatDate = (ts: number | null) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString('zh-HK');
  };

  const getPayloadPreview = (payload: string) => {
    try {
      const parsed = JSON.parse(payload || '{}');
      if (parsed.context?.orderNum) return `訂單 ${parsed.context.orderNum}`;
      if (parsed.context?.name) return `客戶 ${parsed.context.name}`;
      if (parsed.context?.referralCode) return `推薦碼 ${parsed.context.referralCode}`;
      return JSON.stringify(parsed).slice(0, 60) + '...';
    } catch {
      return (payload || '').slice(0, 60);
    }
  };

  return (
    <AdminLayout currentPage="pending-jobs">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Clock className="w-6 h-6 text-red-600" />
          待發 Workflow 訊息
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          列出所有已排程、尚未發送的 workflow 訊息。若客戶表示不希望再接收，可在此取消個別排程。
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row justify-between gap-3">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={phoneFilter}
              onChange={(e) => setPhoneFilter(e.target.value)}
              placeholder="搜尋電話號碼"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm outline-none bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <button type="submit" className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
            <Search size={16} /> 搜尋
          </button>
        </form>
        <button onClick={() => fetchJobs(pagination.page, phoneFilter)} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
          <RefreshCw size={16} /> 重新整理
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          目前沒有待發的 workflow 訊息
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 text-left font-medium">ID</th>
                <th className="px-4 py-3 text-left font-medium">客戶電話</th>
                <th className="px-4 py-3 text-left font-medium">Workflow</th>
                <th className="px-4 py-3 text-left font-medium">預計發送時間</th>
                <th className="px-4 py-3 text-left font-medium">Payload 摘要</th>
                <th className="px-4 py-3 text-center font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-gray-500">#{job.id}</td>
                  <td className="px-4 py-3 font-medium">{job.customerPhone}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{job.workflowName || job.workflowKey || `Workflow #${job.workflowId}`}</div>
                    <div className="text-xs text-gray-400">Step #{job.workflowStepId}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                      <Calendar size={14} />
                      {formatDate(job.scheduledAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-xs truncate" title={job.payload}>
                    {getPayloadPreview(job.payload)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleCancel(job)}
                      className="text-red-600 hover:text-red-700"
                      title="取消排程"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                第 {pagination.page} / {pagination.totalPages} 頁，共 {pagination.total} 筆
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchJobs(pagination.page - 1, phoneFilter)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm disabled:opacity-50 dark:text-white"
                >
                  上一頁
                </button>
                <button
                  onClick={() => fetchJobs(pagination.page + 1, phoneFilter)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm disabled:opacity-50 dark:text-white"
                >
                  下一頁
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
